import { useEffect, useState } from "react";

import { useT, type MessageKey } from "../../shared/i18n";
import type { ApiClient, ClearCategory } from "../../shared/api/client";
import { Banner, Button, Checkbox, Field, Input, Sheet } from "../../shared/ui";

const CATEGORIES: readonly ClearCategory[] = [
  "reminders",
  "records",
  "assistant_memory",
  "assistant_history",
  "transcripts",
];

const CONFIRMATION = "CLEAR";

export type ClearDataApi = Pick<ApiClient, "clearData" | "clearingPreview">;

/** Picks what to wipe and asks for the typed word before sending anything. */
export function ClearDataDialog({
  open,
  onOpenChange,
  api,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  api: ClearDataApi;
}) {
  const t = useT();
  const [chosen, setChosen] = useState<readonly ClearCategory[]>([]);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [result, setResult] = useState<Record<string, number> | null>(null);
  // Rows each category holds now. Unknown (failed or pending) shows no number:
  // the preview is advisory and never blocks the typed confirmation.
  const [counts, setCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!open) return;
    let current = true;
    api
      .clearingPreview()
      .then((preview) => {
        if (current) setCounts(preview.counts);
      })
      .catch(() => {
        if (current) setCounts({});
      });
    return () => {
      current = false;
    };
  }, [open, api]);

  const reset = () => {
    setChosen([]);
    setTyped("");
    setFailed(false);
    setResult(null);
  };
  const toggle = (category: ClearCategory, on: boolean) => {
    setChosen((current) =>
      on ? [...current, category] : current.filter((item) => item !== category),
    );
  };
  const canSubmit = chosen.length > 0 && typed === CONFIRMATION && !busy;
  const submit = async () => {
    setBusy(true);
    setFailed(false);
    try {
      // Keep the canonical category order regardless of click order.
      const ordered = CATEGORIES.filter((item) => chosen.includes(item));
      const { cleared } = await api.clearData(ordered, typed);
      setResult(cleared);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  const label = (category: ClearCategory) =>
    t(`account.clear.${category}` satisfies MessageKey);
  const choiceLabel = (category: ClearCategory) =>
    category in counts
      ? `${label(category)} (${String(counts[category])})`
      : label(category);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title={t("account.clear.heading")}
      description={result ? undefined : t("account.clear.intro")}
      footer={
        result ? (
          <Button
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
          >
            {t("account.dialog.close")}
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              {t("account.dialog.cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={!canSubmit}
              loading={busy}
              onClick={() => {
                void submit();
              }}
            >
              {t("account.clear.submit")}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="flex flex-col gap-2">
          <Banner tone="success">{t("account.clear.done")}</Banner>
          <ul className="text-body text-fg">
            {CATEGORIES.filter((category) => category in result).map(
              (category) => (
                <li key={category}>
                  {`${label(category)}: ${String(result[category])}`}
                </li>
              ),
            )}
          </ul>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {failed && <Banner tone="error">{t("account.clear.failed")}</Banner>}
          <div role="group" aria-label={t("account.clear.heading")}>
            {CATEGORIES.map((category) => (
              <Checkbox
                key={category}
                label={choiceLabel(category)}
                checked={chosen.includes(category)}
                onCheckedChange={(on) => {
                  toggle(category, on);
                }}
              />
            ))}
          </div>
          <Field label={t("account.clear.confirmLabel")}>
            {(props) => (
              <Input
                {...props}
                value={typed}
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                onChange={(event) => {
                  setTyped(event.target.value);
                }}
              />
            )}
          </Field>
        </div>
      )}
    </Sheet>
  );
}
